import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { MdDelete } from "react-icons/md";
import { FiEdit2, FiPlus } from "react-icons/fi";
import PickupPointModal from "./component/PickupPointModal";
import { usePickupPoints } from "../useHook";
import Loader from "../../../Utils/loader";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

function PickupPoints() {
  const {
    getPickupPoints,
    addPickupPoint,
    updatePickupPoint,
    deletePickupPoint,
    togglePickupPointStatus,
  } = usePickupPoints();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.PickupPoints.CREATE);
  const canUpdate = hasPermission(SystemPermissions.PickupPoints.UPDATE);
  const canDelete = hasPermission(SystemPermissions.PickupPoints.DELETE);
  const showActions = canUpdate || canDelete;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [editPoint, setEditPoint] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setEditPoint(null);
  };

  const fetchPickupPoints = async () => {
    try {
      setIsLoading(true);
      const response = await getPickupPoints(setPickupPoints, offset, limit);
      if (response?.pagination) {
        setPagination({
          totalRecords: response.pagination.totalRecords,
          totalPages: response.pagination.totalPages,
          currentPage: response.pagination.currentPage,
          perPage: response.pagination.perPage,
        });
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPickupPoints();
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handleSave = async (data) => {
    try {
      const response = editPoint
        ? await updatePickupPoint(editPoint.id, data)
        : await addPickupPoint(data);

      if (!response?.success) {
        throw new Error(
          response?.message || "Failed to save pickup point.",
        );
      }

      Swal.fire({
        icon: "success",
        title: editPoint ? "Pickup Point Updated" : "Pickup Point Added",
        text: response.message,
        confirmButtonText: "OK",
      });
      closeModal();
      await fetchPickupPoints();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "There was an issue saving the pickup point.",
        confirmButtonText: "OK",
      });
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const response = await togglePickupPointStatus(id);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to update status.");
      }

      setPickupPoints((prev) =>
        prev.map((point) =>
          point.id === id ? { ...point, isActive: newStatus } : point,
        ),
      );

      Swal.fire({
        title: "Success",
        text: `Pickup point status has been ${
          newStatus ? "activated" : "deactivated"
        } successfully!`,
        icon: "success",
        timer: 1000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update pickup point status.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleEdit = (point) => {
    setEditPoint(point);
    openModal();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await deletePickupPoint(id);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to delete pickup point.");
      }
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: response.message || "The pickup point has been deleted.",
        confirmButtonText: "OK",
      });
      await fetchPickupPoints();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed to Delete",
        text:
          error?.message ||
          "An error occurred while deleting the pickup point.",
        confirmButtonText: "OK",
      });
    }
  };

  if (isLoading) {
    return <Loader message="Loading pickup points..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Pickup Points</h2>
        {canCreate && (
          <button onClick={openModal} className="btn btn-primary h-[44px]">
            <FiPlus className="text-white" />
            Add Pickup Point
          </button>
        )}
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow my-5">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No</th>
                <th className="table-th">Name</th>
                <th className="table-th">Address</th>
                <th className="table-th">Phone</th>
                <th className="table-th">Latitude</th>
                <th className="table-th">Longitude</th>
                <th className="table-th">Status</th>
                {showActions && <th className="table-th">Action</th>}
              </tr>
            </thead>
            <tbody>
              {pickupPoints.length > 0 ? (
                pickupPoints.map((point, index) => (
                  <tr
                    key={point.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{offset + index + 1}</td>
                    <td className="table-td">{point.name}</td>
                    <td className="table-td max-w-[280px]">{point.address}</td>
                    <td className="table-td" dir="ltr">
                      {point.phone || "—"}
                    </td>
                    <td className="table-td" dir="ltr">
                      {point.latitude}
                    </td>
                    <td className="table-td" dir="ltr">
                      {point.longitude}
                    </td>
                    <td className="table-td">
                      {canUpdate ? (
                        <div
                          className={`status-toggle ${
                            point.isActive ? "active" : ""
                          }`}
                          onClick={() =>
                            handleToggleStatus(point.id, point.isActive)
                          }
                        >
                          <div className="status-toggle-handle"></div>
                        </div>
                      ) : (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            point.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {point.isActive ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>
                    {showActions && (
                      <td className="table-td flex space-x-1">
                        {canUpdate && (
                          <button
                            className="btn btn-edit text-white"
                            onClick={() => handleEdit(point)}
                          >
                            <FiEdit2 className="text-white" /> Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn btn-delete text-white"
                            onClick={() => handleDelete(point.id)}
                          >
                            <MdDelete className="text-white" /> Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={showActions ? 8 : 7}
                    className="text-center py-6 text-gray-500"
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={pagination.totalRecords}
            onPageChange={handlePageChange}
            className="mt-4"
          />
        </div>
      )}

      <PickupPointModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSave}
        editPoint={editPoint}
      />
    </div>
  );
}

export default PickupPoints;
